import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Image } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSelect } from '../../constants/appSelect';
import { newObjectKeys, formatDateVN, DEFAULT_THUMB } from '../../utils/utility';
import { deletePost, getPosts } from '../../services/post';
import { getAllCategories } from '../../services/category';
import isEqual from 'lodash/isEqual';

const itemName = 'Bài viết';
const createItemLink = '/cms/quan-ly-bai-viet/them-bai-viet';
const editItemLink = '/cms/quan-ly-bai-viet/sua-bai-viet';
const { Text } = Typography;

const PostList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });

  const toPlain100 = (html, limit = 100) => {
    if (!html || typeof html !== 'string') return '';
    const el = document.createElement('div');
    el.innerHTML = html;
    const text = (el.textContent || el.innerText || '').trim();
    if (text.length <= limit) return text;
    return text.slice(0, limit) + '...';
  };

  // Create slug from Vietnamese title and build external URL
  const makeSlug = (str) => {
    if (!str) return '';
    let s = String(str)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove accents
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D');
    s = s.toLowerCase().trim();
    s = s.replace(/[^a-z0-9]+/g, '-') // non-alphanumeric to '-'
         .replace(/^-+|-+$/g, ''); // trim '-'
    return s;
  };

  const externalPostUrl = (title, id) => `http://toancohien.vn/web/post/${makeSlug(title)}-${id}`;

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;
    const categoryStr = searchParams.get('category_id');
    const categoryVal = categoryStr !== null ? Number(categoryStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });
    if (categoryVal > 0) Object.assign(next, { category_id: categoryVal });

    setParams(next);
    formSearch.setFieldsValue({
      keyword: keyword,
      status: statusVal >= 0 ? statusVal : -1,
      category_id: categoryVal > 0 ? categoryVal : -1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = {
      page: String(params.page || 1),
      limit: String(params.limit || 20),
    };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) {
      sp.status = String(params.status[0]);
    }
    if (typeof params.category_id === 'number' && params.category_id > 0) {
      sp.category_id = String(params.category_id);
    }
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const { data: dataPosts, isPending, refetch } = useQuery({ queryKey: ['dataPosts', params], queryFn: () => getPosts(params) });

  // categories
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategories'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const categoryMap = useMemo(() => {
    return (allCategoriesRes?.data || []).reduce((acc, cur) => {
      acc[cur?.id] = cur?.title;
      return acc;
    }, {});
  }, [allCategoriesRes]);
  const categoryOptions = useMemo(() => {
    return [
      { value: -1, label: 'Tất cả' },
      ...(((allCategoriesRes?.data || [])
        .filter((c) => Number(c?.type) === 1)
        .map((c) => ({ value: c.id, label: c.title }))) || []),
    ];
  }, [allCategoriesRes]);

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deletePost,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Xóa ${itemName} thành công!` });
        refetch();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) Object.assign(temp, { keyword: values?.keyword });
    if (values?.category_id > 0) Object.assign(temp, { category_id: values?.category_id });
    if (values?.status >= 0) Object.assign(temp, { status: [values?.status] });

    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetch();
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'id',
      key: 'stt',
      width: 70,
      fixed: 'left',
      align: 'center',
      ellipsis: true,
      onHeaderCell: () => ({ style: { maxWidth: 70 } }),
      onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }),
      render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (_, { title, id }) => (
        <a href={externalPostUrl(title, id)} target="_blank" rel="noreferrer" className="text-[#1677FF]">
          {title}
        </a>
      ),
      minWidth: 150,
      width: 300,
      fixed: 'left',
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_id',
      key: 'category',
      render: (_, { category_id }) => <span>{categoryMap[category_id] || '—'}</span>,
      minWidth: 180,
      width: 220,
    },
    {
      title: 'Ảnh đại diện',
      dataIndex: 'thumb',
      key: 'thumb',
      align: 'center',
      render: (thumb) => {
        const src = typeof thumb === 'string' && thumb.trim() ? thumb : DEFAULT_THUMB;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Image src={src} alt="thumb" height={48} style={{ width: 'auto', height: 48, objectFit: 'cover', borderRadius: 4, backgroundColor: '#f2f3f5', cursor: 'pointer' }} preview={{ mask: null }} fallback={DEFAULT_THUMB} />
          </div>
        );
      },
      minWidth: 100,
      width: 150,
    },
    {
      title: 'Mô tả ngắn',
      dataIndex: 'description',
      key: 'description',
      render: (description) => <Text>{description}</Text>,
      minWidth: 200,
      width: 350,
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, { updated_at }) => <Space direction="vertical">{updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}</Space>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={(AppSelect.STATUS || []).find((item) => item?.value === status)?.color}>
          {(AppSelect.STATUS || []).find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 70,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
            <EditOutlined />
          </Button>
          {record?.status !== 2 && (
            <Button type="text" onClick={() => mutateDelete(record?.id)}>
              <DeleteOutlined />
            </Button>
          )}
        </Space>
      ),
      width: 70,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        <Link to={createItemLink}>
          <Button type="primary">
            <PlusOutlined />
            Thêm {itemName}
          </Button>
        </Link>
      </div>

      <Form name="search_posts" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập từ khóa tìm kiếm..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="category_id" label={<Text>Danh mục</Text>} initialValue={-1}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={categoryOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...(AppSelect.STATUS || [])]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params && Object.values(newObjectKeys(params, ['keyword', 'status', 'category_id'])).some((value) => value) && (
              <Button
                htmlType="button"
                onClick={() => {
                  formSearch.resetFields();
                  setParams({ page: 1, limit: params.limit });
                }}
              >
                <DeleteOutlined />
                Xóa tìm kiếm
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom columns={columns} dataSource={dataPosts?.data ?? []} totalItem={dataPosts?.total ?? 0} loading={isPending} params={params} setParams={setParams} />
    </div>
  );
};

export default PostList;
